import { useState, useCallback, useMemo, useEffect } from 'react';
import { Nav } from '../components/Nav';
import { useScheduleData } from '../hooks/useScheduleData';
import { useAuth } from '../hooks/useAuth';
import {
  sendNotification,
  sendScheduleToSelected,
  sendCustomMessage,
} from '../services/api';
import { formatDateDisplay, isWeekInFuture, getCurrentOrNextSundayDate } from '../utils/date';
import type { SelectedRecipient, Week, UpcomingSpecial, SpecialReminderResult } from '../types';

export function AdminPage() {
  const { data, hymns, loading, error } = useScheduleData();
  const { isAdminAuthenticated, loginForAdmin } = useAuth();

  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState(false);
  const [selectedWeekIndex, setSelectedWeekIndex] = useState<number | null>(null);
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [scheduleStatusMessage, setScheduleStatusMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [customStatusMessage, setCustomStatusMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [customMessage, setCustomMessage] = useState('');
  const [customMessageCollapsed, setCustomMessageCollapsed] = useState(true);
  const [sending, setSending] = useState<string | null>(null);

  const [scheduleRecipients, setScheduleRecipients] = useState<Set<string>>(new Set());
  const [customRecipients, setCustomRecipients] = useState<Set<string>>(new Set());
  const [specialsReport, setSpecialsReport] = useState<{ upcomingSpecials: UpcomingSpecial[]; specialReminderResults: SpecialReminderResult[] } | null>(null);

  const handleLogin = useCallback(async () => {
    const success = await loginForAdmin(password);
    if (!success) {
      setLoginError(true);
    }
  }, [password, loginForAdmin]);

  const futureWeeks = useMemo(() => {
    if (!data) return [];
    return data.weeks
      .map((week, index) => ({ week, index }))
      .filter(({ week }) => isWeekInFuture(week.date));
  }, [data]);

  // Auto-select current/next Sunday
  useEffect(() => {
    if (data && futureWeeks.length > 0 && selectedWeekIndex === null) {
      const currentSundayStr = getCurrentOrNextSundayDate();
      const matchingWeek = futureWeeks.find(({ week }) => week.date === currentSundayStr);
      if (matchingWeek) {
        setSelectedWeekIndex(matchingWeek.index);
      }
    }
  }, [data, futureWeeks, selectedWeekIndex]);

  // Initialize recipient checkboxes
  useEffect(() => {
    if (data) {
      const allIds = new Set<string>();
      data.pianists.filter(p => p.phone).forEach(p => allIds.add(`pianist-${p.id}`));
      data.conductors.filter(c => c.phone).forEach(c => allIds.add(`conductor-${c.id}`));
      data.notificationContacts.filter(c => c.phone).forEach(c => allIds.add(`contact-${c.id}`));
      setScheduleRecipients(allIds);
      setCustomRecipients(new Set(allIds));
    }
  }, [data]);

  const selectedWeek = useMemo(() => {
    if (selectedWeekIndex === null || !data) return null;
    return data.weeks[selectedWeekIndex];
  }, [selectedWeekIndex, data]);

  const getHymnTitle = useCallback(
    (hymnId: string) => {
      const hymn = hymns.find((h) => String(h.number) === String(hymnId));
      return hymn ? `${hymn.number} - ${hymn.title}` : 'Untitled';
    },
    [hymns]
  );

  const getCounts = useMemo(() => {
    if (!selectedWeek || !data) return { pianists: 0, special: 0, everyone: 0 };

    const pianistCount = selectedWeek.pianist ? 1 : 0;
    const specialCount = selectedWeek.songs.filter(
      (s) => s.isSpecial && s.performerPhone
    ).length;
    const everyoneCount =
      pianistCount + specialCount + data.notificationContacts.length;

    return { pianists: pianistCount, special: specialCount, everyone: everyoneCount };
  }, [selectedWeek, data]);

  const handleSendNotification = useCallback(
    async (type: 'pianists' | 'special' | 'everyone') => {
      if (selectedWeekIndex === null) {
        alert('Please select a week first');
        return;
      }

      setSending(type);
      try {
        const result = await sendNotification(selectedWeekIndex, type);
        if (result.success) {
          setStatusMessage({
            type: 'success',
            text: `Notifications sent successfully to ${result.sentCount} recipient(s)!`,
          });
        } else {
          setStatusMessage({
            type: 'error',
            text: `Error: ${result.error || 'Failed to send notifications'}`,
          });
        }
        setTimeout(() => setStatusMessage(null), 5000);
      } catch (err) {
        alert(`Error sending notifications: ${err instanceof Error ? err.message : 'Unknown error'}`);
      } finally {
        setSending(null);
      }
    },
    [selectedWeekIndex]
  );

  const getSelectedRecipients = useCallback(
    (recipientSet: Set<string>): SelectedRecipient[] => {
      if (!data) return [];
      const recipients: SelectedRecipient[] = [];

      data.pianists.filter(p => p.phone).forEach(p => {
        if (recipientSet.has(`pianist-${p.id}`)) {
          recipients.push({ name: p.name, phone: p.phone });
        }
      });

      data.conductors.filter(c => c.phone).forEach(c => {
        if (recipientSet.has(`conductor-${c.id}`)) {
          recipients.push({ name: c.name, phone: c.phone });
        }
      });

      data.notificationContacts.filter(c => c.phone).forEach(c => {
        if (recipientSet.has(`contact-${c.id}`)) {
          recipients.push({ name: c.name, phone: c.phone });
        }
      });

      return recipients;
    },
    [data]
  );

  const handleSendScheduleToSelected = useCallback(async () => {
    if (selectedWeekIndex === null) {
      alert('Please select a week first');
      return;
    }

    const recipients = getSelectedRecipients(scheduleRecipients);
    if (recipients.length === 0) {
      alert('Please select at least one recipient');
      return;
    }

    setSending('schedule-selected');
    setSpecialsReport(null);
    try {
      const result = await sendScheduleToSelected(selectedWeekIndex, recipients);
      if (result.success) {
        setScheduleStatusMessage({
          type: 'success',
          text: `Schedule sent successfully to ${result.sentCount} recipient(s)!`,
        });
        setSpecialsReport({
          upcomingSpecials: result.upcomingSpecials ?? [],
          specialReminderResults: result.specialReminderResults ?? [],
        });
      } else {
        setScheduleStatusMessage({
          type: 'error',
          text: `Error: ${result.error || 'Failed to send schedule'}`,
        });
      }
      setTimeout(() => setScheduleStatusMessage(null), 5000);
    } catch (err) {
      alert(`Error sending schedule: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setSending(null);
    }
  }, [selectedWeekIndex, scheduleRecipients, getSelectedRecipients]);

  const handleSendCustomMessage = useCallback(async () => {
    if (!customMessage.trim()) {
      alert('Please enter a message to send');
      return;
    }

    const recipients = getSelectedRecipients(customRecipients);
    if (recipients.length === 0) {
      alert('Please select at least one recipient');
      return;
    }

    setSending('custom');
    try {
      const result = await sendCustomMessage(customMessage.trim(), recipients);
      if (result.success) {
        setCustomStatusMessage({
          type: 'success',
          text: `Custom message sent successfully to ${result.sentCount} recipient(s)!`,
        });
        setCustomMessage('');
      } else {
        setCustomStatusMessage({
          type: 'error',
          text: `Error: ${result.error || 'Failed to send custom message'}`,
        });
      }
      setTimeout(() => setCustomStatusMessage(null), 5000);
    } catch (err) {
      alert(`Error sending custom message: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setSending(null);
    }
  }, [customMessage, customRecipients, getSelectedRecipients]);

  const renderRecipientCheckboxes = useCallback(
    (recipientSet: Set<string>, setRecipientSet: React.Dispatch<React.SetStateAction<Set<string>>>) => {
      if (!data) return null;

      const toggleRecipient = (key: string) => {
        setRecipientSet((prev) => {
          const next = new Set(prev);
          if (next.has(key)) {
            next.delete(key);
          } else {
            next.add(key);
          }
          return next;
        });
      };

      const selectAll = () => {
        const allIds = new Set<string>();
        data.pianists.filter(p => p.phone).forEach(p => allIds.add(`pianist-${p.id}`));
        data.conductors.filter(c => c.phone).forEach(c => allIds.add(`conductor-${c.id}`));
        data.notificationContacts.filter(c => c.phone).forEach(c => allIds.add(`contact-${c.id}`));
        setRecipientSet(allIds);
      };

      const deselectAll = () => {
        setRecipientSet(new Set());
      };

      return (
        <div style={{ marginBottom: '15px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <strong>Select Recipients:</strong>
            <div>
              <button className="btn-secondary" style={{ padding: '4px 8px', fontSize: '12px', marginRight: '5px' }} onClick={selectAll}>
                Select All
              </button>
              <button className="btn-secondary" style={{ padding: '4px 8px', fontSize: '12px' }} onClick={deselectAll}>
                Deselect All
              </button>
            </div>
          </div>
          <div className="recipient-checkboxes">
            {data.pianists.filter(p => p.phone).length > 0 && (
              <>
                <div className="recipient-checkboxes__group-label">Pianists/Organists:</div>
                {data.pianists.filter(p => p.phone).map(p => (
                  <label key={`pianist-${p.id}`} className="recipient-checkboxes__item">
                    <input
                      type="checkbox"
                      checked={recipientSet.has(`pianist-${p.id}`)}
                      onChange={() => toggleRecipient(`pianist-${p.id}`)}
                    />
                    <span>{p.name}</span>
                    <span className="recipient-checkboxes__phone">{p.phone}</span>
                  </label>
                ))}
              </>
            )}
            {data.conductors.filter(c => c.phone).length > 0 && (
              <>
                <div className="recipient-checkboxes__group-label">Conductors:</div>
                {data.conductors.filter(c => c.phone).map(c => (
                  <label key={`conductor-${c.id}`} className="recipient-checkboxes__item">
                    <input
                      type="checkbox"
                      checked={recipientSet.has(`conductor-${c.id}`)}
                      onChange={() => toggleRecipient(`conductor-${c.id}`)}
                    />
                    <span>{c.name}</span>
                    <span className="recipient-checkboxes__phone">{c.phone}</span>
                  </label>
                ))}
              </>
            )}
            {data.notificationContacts.filter(c => c.phone).length > 0 && (
              <>
                <div className="recipient-checkboxes__group-label">Notification Contacts:</div>
                {data.notificationContacts.filter(c => c.phone).map(c => (
                  <label key={`contact-${c.id}`} className="recipient-checkboxes__item">
                    <input
                      type="checkbox"
                      checked={recipientSet.has(`contact-${c.id}`)}
                      onChange={() => toggleRecipient(`contact-${c.id}`)}
                    />
                    <span>{c.name}</span>
                    <span className="recipient-checkboxes__phone">{c.phone}</span>
                  </label>
                ))}
              </>
            )}
          </div>
        </div>
      );
    },
    [data]
  );

  const renderWeekSummary = useCallback(
    (week: Week) => {
      const pianist = data?.pianists.find((p) => p.id === week.pianist);

      return (
        <div className="week-summary">
          <h4>Week Summary</h4>
          {pianist && <p><strong>Pianist:</strong> {pianist.name}</p>}
          {week.notes && <p><strong>Notes:</strong> {week.notes}</p>}
          <ul>
            {week.songs.map((song, idx) => (
              <li key={idx}>
                {song.isSpecial ? (
                  <>
                    <strong>[Special]</strong> {song.songName || 'Untitled'}
                    {song.performerName && ` - ${song.performerName}`}
                  </>
                ) : (
                  song.hymnId ? getHymnTitle(song.hymnId) : 'No hymn selected'
                )}
              </li>
            ))}
          </ul>
        </div>
      );
    },
    [data, getHymnTitle]
  );

  if (loading) {
    return (
      <>
        <Nav />
        <div className="page-container">
          <p>Loading...</p>
        </div>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <Nav />
        <div className="page-container">
          <p style={{ color: 'red' }}>Error: {error || 'Failed to load data'}</p>
        </div>
      </>
    );
  }

  if (!isAdminAuthenticated) {
    return (
      <>
        <Nav />
        <div className="login-container">
          <h2>Admin Login</h2>
          <input
            type="password"
            placeholder="Enter password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
          />
          <button className="btn-primary" onClick={handleLogin}>
            Login
          </button>
          {loginError && (
            <p style={{ color: '#dc3545' }}>Incorrect password</p>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <Nav />
      <div className="admin-content admin-content--show">
        <h1>Admin - Send Notifications</h1>

        <div className="week-select-card">
          <h3>Select Week</h3>
          <select
            value={selectedWeekIndex ?? ''}
            onChange={(e) =>
              setSelectedWeekIndex(e.target.value ? parseInt(e.target.value) : null)
            }
            style={{ width: '100%', padding: '12px', fontSize: '16px', borderRadius: '4px', border: '1px solid #ddd' }}
          >
            <option value="">-- Select a week --</option>
            {futureWeeks.map(({ week, index }) => (
              <option key={index} value={index}>
                {formatDateDisplay(week.date)}
              </option>
            ))}
          </select>

          {selectedWeek && renderWeekSummary(selectedWeek)}

          <div className="notification-buttons">
            <button
              className="notification-btn btn-primary"
              onClick={() => handleSendNotification('pianists')}
              disabled={getCounts.pianists === 0 || sending !== null}
            >
              <span>{sending === 'pianists' ? 'Sending...' : 'Notify Pianists/Organists'}</span>
              <span className="recipient-count">
                {getCounts.pianists} recipient{getCounts.pianists !== 1 ? 's' : ''}
              </span>
            </button>

            <button
              className="notification-btn btn-warning"
              onClick={() => handleSendNotification('special')}
              disabled={getCounts.special === 0 || sending !== null}
            >
              <span>{sending === 'special' ? 'Sending...' : 'Notify Special Musical Numbers'}</span>
              <span className="recipient-count">
                {getCounts.special} recipient{getCounts.special !== 1 ? 's' : ''}
              </span>
            </button>

            <button
              className="notification-btn btn-success"
              onClick={() => handleSendNotification('everyone')}
              disabled={getCounts.everyone === 0 || sending !== null}
            >
              <span>{sending === 'everyone' ? 'Sending...' : 'Notify Everyone'}</span>
              <span className="recipient-count">
                {getCounts.everyone} recipient{getCounts.everyone !== 1 ? 's' : ''}
              </span>
            </button>
          </div>

          {statusMessage && (
            <div className={`status-message status-message--${statusMessage.type}`}>
              {statusMessage.text}
            </div>
          )}
        </div>

        <div className="week-select-card">
          <h3>Send Schedule to Selected Recipients</h3>
          <p style={{ color: '#666', marginBottom: '15px' }}>
            Send the week's schedule summary to selected recipients.
          </p>

          {renderRecipientCheckboxes(scheduleRecipients, setScheduleRecipients)}

          <div className="notification-buttons">
            <button
              className="notification-btn btn-primary"
              onClick={handleSendScheduleToSelected}
              disabled={selectedWeekIndex === null || sending !== null}
            >
              <span>{sending === 'schedule-selected' ? 'Sending...' : 'Send Schedule to Selected'}</span>
              <span className="recipient-count">{scheduleRecipients.size} selected</span>
            </button>
          </div>

          {scheduleStatusMessage && (
            <div className={`status-message status-message--${scheduleStatusMessage.type}`}>
              {scheduleStatusMessage.text}
            </div>
          )}

          {specialsReport && (
            <div style={{ marginTop: '20px', borderTop: '1px solid #ddd', paddingTop: '16px' }}>
              <h4 style={{ margin: '0 0 12px', fontSize: '14px', color: '#444' }}>
                Upcoming Special Musical Numbers (Next 4 Weeks)
              </h4>
              {specialsReport.upcomingSpecials.length === 0 ? (
                <p style={{ color: '#888', fontSize: '13px', margin: 0 }}>No special musical numbers scheduled in the next 4 weeks.</p>
              ) : (
                <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                  {specialsReport.upcomingSpecials.map((s, i) => {
                    const reminderResult = specialsReport.specialReminderResults.find(
                      r => r.name === s.performer && r.date === s.date && r.title === s.title
                    );
                    return (
                      <li key={i} style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontSize: '13px' }}>
                        <div style={{ fontWeight: 600, color: '#333' }}>{s.date}</div>
                        <div style={{ color: '#555', marginTop: '2px' }}>
                          "{s.title}" — {s.performer}
                        </div>
                        <div style={{ marginTop: '3px', fontSize: '12px', color: reminderResult?.success ? '#2d7a2d' : s.hasPhone ? '#c0392b' : '#999' }}>
                          {reminderResult?.success
                            ? 'Reminder sent'
                            : reminderResult
                            ? `Reminder failed: ${reminderResult.error}`
                            : 'No phone on file — reminder not sent'}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className={`week-select-card collapsible-section ${customMessageCollapsed ? 'collapsible-section--collapsed' : ''}`}>
          <div
            className="collapsible-section__header"
            onClick={() => setCustomMessageCollapsed(!customMessageCollapsed)}
          >
            <h3 style={{ margin: 0 }}>Custom Message</h3>
            <span className="collapsible-section__icon">V</span>
          </div>
          <div className="collapsible-section__content">
            <p style={{ color: '#666', marginBottom: '15px', marginTop: '15px' }}>
              Send a custom text message to selected recipients.
            </p>

            {renderRecipientCheckboxes(customRecipients, setCustomRecipients)}

            <textarea
              placeholder="Enter your custom message here..."
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              style={{ width: '100%', minHeight: '120px', padding: '12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ddd', resize: 'vertical' }}
            />
            <div className="notification-buttons" style={{ marginTop: '15px' }}>
              <button
                className="notification-btn btn-info"
                onClick={handleSendCustomMessage}
                disabled={sending !== null}
              >
                <span>{sending === 'custom' ? 'Sending...' : 'Send Custom Message'}</span>
                <span className="recipient-count">{customRecipients.size} selected</span>
              </button>
            </div>

            {customStatusMessage && (
              <div className={`status-message status-message--${customStatusMessage.type}`}>
                {customStatusMessage.text}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
